import React from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

interface PermissionMatrixModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PermissionRow {
  capability: string;
  reader: boolean;
  editor: boolean;
  admin: boolean;
}

const PERMISSIONS: PermissionRow[] = [
  { capability: "View Workflows", reader: true, editor: true, admin: true },
  { capability: "Run Workflows", reader: true, editor: true, admin: true },
  { capability: "Edit Workflows", reader: false, editor: true, admin: true },
  { capability: "Create Workflows", reader: false, editor: true, admin: true },
  { capability: "Delete Workflows", reader: false, editor: false, admin: true },
  { capability: "Manage Users", reader: false, editor: false, admin: true },
  { capability: "View Audit Log", reader: false, editor: false, admin: true },
  { capability: "Change User Roles", reader: false, editor: false, admin: true },
];

const CheckIcon = () => <Icon className="text-success" icon="solar:check-circle-bold" width={18} />;

const CrossIcon = () => (
  <Icon className="text-default-300" icon="solar:close-circle-outline" width={18} />
);

export const PermissionMatrixModal: React.FC<PermissionMatrixModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} size="lg" onClose={onClose}>
      <ModalContent>
        <ModalHeader>{t("Permission Matrix")}</ModalHeader>
        <ModalBody>
          <Table removeWrapper aria-label={t("Permission matrix")}>
            <TableHeader>
              <TableColumn>{t("Capability")}</TableColumn>
              <TableColumn className="text-center">{t("Reader")}</TableColumn>
              <TableColumn className="text-center">{t("Editor")}</TableColumn>
              <TableColumn className="text-center">{t("Admin")}</TableColumn>
            </TableHeader>
            <TableBody>
              {PERMISSIONS.map((perm) => (
                <TableRow key={perm.capability}>
                  <TableCell className="font-medium">{t(perm.capability)}</TableCell>
                  <TableCell className="text-center">
                    {perm.reader ? <CheckIcon /> : <CrossIcon />}
                  </TableCell>
                  <TableCell className="text-center">
                    {perm.editor ? <CheckIcon /> : <CrossIcon />}
                  </TableCell>
                  <TableCell className="text-center">
                    {perm.admin ? <CheckIcon /> : <CrossIcon />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-4 p-3 bg-default-100 rounded-lg text-sm text-default-600">
            <Icon
              className="inline-block mr-1 align-text-bottom"
              icon="solar:info-circle-outline"
              width={16}
            />
            {t(
              "Workflow permissions apply to Users only. Admins have full access to all workflows by default.",
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            {t("Close")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
